import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeOff } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/api/auth/login`, {
        email,
        password
      });
      
      login(response.data.user, response.data.token);
      toast.success('Welcome back!');
      
      if (response.data.user.is_admin) {
        navigate('/admin');
      } else {
        navigate('/');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left Side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Logo */}
          <Link to="/" className="block mb-12">
            <h1 className="font-heading text-2xl font-semibold">BLUSH & GO</h1>
          </Link>

          <h2 className="font-heading text-3xl font-medium mb-2">Welcome Back</h2>
          <p className="text-neutral-500 mb-8">Sign in to continue to your account</p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email" className="text-sm uppercase tracking-widest">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 h-12 rounded-none border-neutral-300"
                placeholder="you@example.com"
                required
                data-testid="login-email-input"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-sm uppercase tracking-widest">
                Password
              </Label>
              <div className="relative mt-2">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 rounded-none border-neutral-300 pr-10"
                  placeholder="Enter your password"
                  required
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#1A1A1A] text-white hover:bg-neutral-800 rounded-none uppercase tracking-widest text-xs font-bold"
              data-testid="login-submit-btn"
            >
              {loading ? <span className="spinner"></span> : 'Sign In'}
            </Button>
          </form>

          <p className="text-center text-sm text-neutral-500 mt-8">
            Don't have an account?{' '}
            <Link to="/register" className="text-neutral-900 hover:underline" data-testid="register-link">
              Create one
            </Link>
          </p>

          <div className="mt-8 pt-8 border-t text-center">
            <Link 
              to="/admin/login" 
              className="text-xs uppercase tracking-widest text-neutral-500 hover:text-neutral-900"
              data-testid="admin-login-link"
            >
              Admin Login
            </Link>
          </div>
        </div>
      </div>

      {/* Right Side - Image */}
      <div className="hidden lg:block w-1/2 relative">
        <img
          src="https://images.unsplash.com/photo-1596462502278-27bfdc403348?w=800"
          alt="Beauty products"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/20"></div>
      </div>
    </div>
  );
};

export default LoginPage;
